import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, MapPin, Clock, Heart, CheckCircle, Users, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

interface AmbassadorRolePageProps {
  onNavigate?: (page: string) => void;
}

export default function AmbassadorRolePage({ onNavigate }: AmbassadorRolePageProps) {
  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      window.location.hash = `#/${page}`;
    }
    window.scrollTo(0, 0);
  };

  const handleApply = () => {
    // Open email client with pre-filled subject
    window.location.href = "mailto:careers@cannabias.co.uk?subject=Application: CannaBias Ambassador";
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button 
            variant="ghost" 
            className="gap-2 mb-8 -ml-2" 
            onClick={() => handleNavigation("careers")}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Careers
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-start gap-6 mb-6">
            <div className="w-16 h-16 rounded-xl gradient-green flex items-center justify-center flex-shrink-0">
              <Users className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="mb-2">CannaBias Ambassador</h1>
              <div className="flex flex-wrap gap-3">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                  9 positions available
                </Badge>
                <Badge variant="outline">Start ASAP</Badge>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-6 text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span>UK wide (remote)</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>Flexible hours — no minimum commitment</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              <span>Volunteer position</span>
            </div>
          </div>
        </motion.div>

        {/* Apply CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Card className="p-6 mb-12 bg-primary/5 border-primary/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="mb-1">Ready to make a difference?</h3>
                <p className="text-muted-foreground">
                  We'd love to hear from you. Apply now to join our team.
                </p>
              </div>
              <Button size="lg" className="gap-2 flex-shrink-0" onClick={handleApply}>
                Apply Now
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Role Overview */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 className="mb-4">About the Role</h2>
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <p className="text-muted-foreground text-lg">
              CannaBias Ambassadors are essential members of our team, serving as a vital link between different perspectives within the medical cannabis community. You'll play a key role in raising awareness, fostering transparency, and helping build a platform that truly serves patients.
            </p>
          </div>
        </motion.div>

        {/* What You'll Do */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 className="mb-6">What you'll do</h2>
          <div className="space-y-3">
            {[
              "Share CannaBias with your network and the wider medical cannabis community",
              "Gather feedback from patients and community members to help improve the platform",
              "Represent patient perspectives and advocate for transparency",
              "Help create awareness about the importance of unbiased product reviews",
              "Connect patients with accurate, trustworthy information about medical cannabis products",
              "Participate in community discussions and help shape platform direction"
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Who We're Looking For */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h2 className="mb-6">Who we're looking for</h2>
          <div className="space-y-3 mb-4">
            {[
              "Passionate about patient empowerment and transparency",
              "Strong communicator with empathy and active listening skills",
              "Connected to the UK medical cannabis community (or eager to be)",
              "Committed to representing diverse patient perspectives",
              "Self-motivated and able to work independently",
              "Comfortable engaging online (social media, forums, communities)"
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{item}</span>
              </div>
            ))}
          </div>
          <Card className="p-4 bg-muted/50 border-muted">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Note:</strong> Whilst we accept all applications, preference will be given to medical cannabis patients and those who strongly align with our values of transparency, patient empowerment, and community-driven development.
            </p>
          </Card>
        </motion.div>

        {/* Compensation & Benefits */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <h2 className="mb-6">Compensation & benefits</h2>
          <div className="space-y-4">
            <Card className="p-6">
              <h3 className="mb-3 flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                Volunteer Position
              </h3>
              <p className="text-muted-foreground mb-4">
                This is currently a volunteer role — we're a patient-led startup building something meaningful together. Whilst this is not a paid position, benefits may be received in due course as CannaBias grows.
              </p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>Be part of building the UK's leading patient-led cannabis review platform</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>Direct impact on the medical cannabis community</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>Network with passionate individuals across the UK</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>Develop skills in community management and advocacy</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span>Flexible commitment — we value every minute of your time</span>
                </div>
              </div>
            </Card>
          </div>
        </motion.div>

        {/* Time Commitment */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <h2 className="mb-4">Time commitment</h2>
          <p className="text-muted-foreground text-lg">
            There is no minimum hourly commitment required. We understand that everyone has different availability and circumstances. Whether you can contribute a few hours a week or a few hours a month — we genuinely value every minute of your time.
          </p>
        </motion.div>

        {/* Apply Section */}
        <motion.div
          className="pt-12 border-t border-border"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="mb-4">Ready to apply?</h2>
            <p className="text-muted-foreground text-lg mb-8">
              Send us an email explaining why you'd like to become a CannaBias Ambassador and how you'd like to contribute. Tell us your story — we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="gap-2" onClick={handleApply}>
                Apply via Email
                <ExternalLink className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => handleNavigation("careers")}>
                View All Positions
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
